import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  TrendingUp,
} from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import blogPosts from "../data/blogData";

/** Newest `date` first so the latest post stays at the top of the grid. */
const sortPostsByLatestFirst = (list) =>
  [...list].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

export default function Blog() {
  const [active] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [posts, setPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 12; // 3 rows × 4 columns on large screens

  useEffect(() => {
    setIsVisible(true);

    // Load static blog data (newest published first)
    setPosts(sortPostsByLatestFirst(blogPosts));

    console.log("✅ Static blog loaded:", blogPosts);
  }, []);

  const filtered = posts.filter((p) => {
    const matchesCategory = active === "All" || p.category === active;
    const matchesSearch =
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Pagination logic
  const totalPages = Math.ceil(filtered.length / postsPerPage);

  // Compute page numbers for unlimited pages with ellipses when needed
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    // If near start, show first three, ellipsis, last
    if (currentPage <= 3) {
      pages.push(1, 2, 3, "...", totalPages);
      return pages;
    }

    // If near end, show first, ellipsis, last three
    if (currentPage >= totalPages - 2) {
      pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      return pages;
    }

    // Middle: first, ellipsis, prev/current/next, ellipsis, last
    pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
    return pages;
  };
  const pageNumbers = getPageNumbers();


  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [active, searchQuery]);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* NAVBAR */}
      <Navbar />

      {/* ANIMATED HEADER */}
      <header className="pt-20 sm:pt-24 lg:pt-0 relative min-h-screen flex items-center overflow-hidden">
        {/* Animated background with particles */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-purple-900 to-pink-900">
          {/* Animated gradient blobs */}
          <div className="absolute inset-0 opacity-40">
            <div className="absolute top-0 -left-10 sm:-left-20 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
            <div className="absolute top-0 -right-10 sm:-right-20 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-10 sm:-bottom-20 left-1/2 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
            <div className="absolute top-1/2 left-1/4 w-[200px] sm:w-[300px] lg:w-[400px] h-[200px] sm:h-[300px] lg:h-[400px] bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-6000"></div>
          </div>

          {/* Floating particles */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-white/30 rounded-full animate-float"></div>
            <div className="absolute top-1/3 right-1/4 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-white/20 rounded-full animate-float animation-delay-1000"></div>
            <div className="absolute bottom-1/3 left-1/3 w-2 sm:w-2.5 h-2 sm:h-2.5 bg-white/25 rounded-full animate-float animation-delay-2000"></div>
            <div className="absolute top-2/3 right-1/3 w-0.5 sm:w-1 h-0.5 sm:h-1 bg-white/30 rounded-full animate-float animation-delay-3000"></div>
            <div className="absolute bottom-1/4 left-2/3 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-white/20 rounded-full animate-float animation-delay-4000"></div>
            <div className="absolute top-1/2 left-1/2 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-white/25 rounded-full animate-float animation-delay-5000"></div>
          </div>

          {/* Grid overlay */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41IiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-20"></div>

          {/* Radial gradient overlay */}
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/40"></div>
        </div>

        {/* Content */}
        <div className={`relative z-10 w-full px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 text-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="max-w-5xl mx-auto">
            {/* Animated badge */}
            <div className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-6 sm:mb-8 group hover:bg-white/15 transition-all duration-300 cursor-default ${isVisible ? "animate-fade-in-scale" : "opacity-0 scale-95"}`}>
              <div className="relative">
                <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-300 animate-pulse-glow" />
                <div className="absolute inset-0 bg-yellow-300 blur-md opacity-50 animate-pulse"></div>
              </div>
              <span className="text-white/95 text-xs sm:text-sm font-medium tracking-wide">
                Real Estate Insights & Market Intelligence
              </span>
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse"></div>
            </div>

            {/* Main heading with staggered animation */}
            <div className="mb-6 sm:mb-8">
              <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black text-white tracking-tight leading-[1.1] transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
                <span className="inline-block hover:scale-105 transition-transform duration-300">
                  Discover
                </span>{" "}
                <span className="inline-block hover:scale-105 transition-transform duration-300">
                  Your
                </span>
              </h1>
              <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tight leading-[1.1] mt-2 transition-all duration-1000 delay-400 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
                <span className="inline-block bg-gradient-to-r from-yellow-300 via-pink-300 to-purple-300 bg-clip-text text-transparent animate-gradient-shift bg-[length:200%_auto] hover:scale-105 transition-transform duration-300">
                  Dream Property
                </span>
              </h1>
            </div>

            {/* Subtitle with fade in */}
            <p className={`text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8 sm:mb-10 leading-relaxed font-light px-4 transition-all duration-1000 delay-600 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              Expert insights, market trends, and practical guides to help you make{" "}
              <span className="text-yellow-300 font-semibold">informed</span> real estate decisions
            </p>

            {/* Enhanced Search Bar */}
            <div className={`w-full max-w-3xl mx-auto relative group transition-all duration-1000 delay-800 px-4 sm:px-0 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 rounded-2xl sm:rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>

              <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border-2 border-white/50 overflow-hidden hover:border-purple-300 transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                <div className="relative flex items-center">
                  <div className="pl-4 sm:pl-6 pr-3 sm:pr-4">
                    <Search className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400 group-hover:text-purple-600 transition-all duration-300 group-hover:scale-110" />
                  </div>

                  <input
                    type="text"
                    placeholder="Search articles, guides..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 py-4 sm:py-6 pr-3 sm:pr-4 bg-transparent text-slate-700 dark:text-slate-200 placeholder:text-slate-400 outline-none text-sm sm:text-base lg:text-lg"
                  />

                  <div className="flex items-center gap-2 sm:gap-3 pr-4 sm:pr-6">
                    <kbd className="hidden md:inline-flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 text-xs font-semibold text-slate-500 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-sm group-hover:border-purple-300 group-hover:text-purple-600 transition-all">
                      <span>⌘</span>
                      <span>K</span>
                    </kbd>
                    <button className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg sm:rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300 opacity-0 group-hover:opacity-100 text-xs sm:text-sm lg:text-base">
                      Search
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick stats */}
            <div className={`flex flex-wrap items-center justify-center gap-4 sm:gap-6 lg:gap-8 mt-6 sm:mt-8 lg:mt-10 transition-all duration-1000 delay-1000 px-4 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              <div className="flex items-center gap-2 text-white/80 hover:text-white transition-colors group cursor-default">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-xs sm:text-sm font-medium">250+ Articles</span>
              </div>
              <div className="w-px h-3 sm:h-4 bg-white/20"></div>
              <div className="flex items-center gap-2 text-white/80 hover:text-white transition-colors group cursor-default">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full animate-pulse animation-delay-1000"></div>
                <span className="text-xs sm:text-sm font-medium">10K+ Readers</span>
              </div>
              <div className="w-px h-3 sm:h-4 bg-white/20"></div>
              <div className="flex items-center gap-2 text-white/80 hover:text-white transition-colors group cursor-default">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full animate-pulse animation-delay-2000"></div>
                <span className="text-xs sm:text-sm font-medium">Updated Daily</span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced scroll indicator — button so full hit area + sits above hero content */}
        <button
          type="button"
          aria-label="Scroll to blog articles"
          onClick={() =>
            document.getElementById('main-content')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }
          className="absolute bottom-6 sm:bottom-8 lg:bottom-10 left-1/2 z-30 -translate-x-1/2 flex flex-col items-center gap-2 sm:gap-3 animate-bounce-slow cursor-pointer group rounded-full border-0 bg-transparent px-5 py-3 text-left hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          <span className="text-white/60 text-[10px] sm:text-xs font-medium uppercase tracking-wider group-hover:text-white transition-colors">
            Scroll to explore
          </span>
          <div className="w-6 h-9 sm:w-7 sm:h-11 border-2 border-white/40 rounded-full flex items-start justify-center p-1.5 sm:p-2 group-hover:border-white/80 transition-all">
            <div className="w-1 h-2 sm:w-1.5 sm:h-3 bg-white/70 rounded-full animate-scroll-down"></div>
          </div>
        </button>

        {/* Floating shapes for depth */}
        <div className="absolute top-20 right-20 w-16 h-16 lg:w-20 lg:h-20 border-2 border-white/10 rounded-2xl rotate-12 animate-float-slow hidden lg:block"></div>
        <div className="absolute bottom-32 left-20 w-12 h-12 lg:w-16 lg:h-16 border-2 border-white/10 rounded-full animate-float-slow animation-delay-2000 hidden lg:block"></div>
        <div className="absolute top-1/2 right-32 w-10 h-10 lg:w-12 lg:h-12 border-2 border-white/10 rounded-lg -rotate-12 animate-float-slow animation-delay-4000 hidden lg:block"></div>
      </header>

      <style>{`
  @keyframes blob {
    0%, 100% { 
      transform: translate(0, 0) scale(1); 
    }
    33% { 
      transform: translate(40px, -60px) scale(1.15); 
    }
    66% { 
      transform: translate(-30px, 30px) scale(0.95); 
    }
  }

  @keyframes float {
    0%, 100% { 
      transform: translateY(0px) translateX(0px); 
      opacity: 0.3;
    }
    50% { 
      transform: translateY(-30px) translateX(20px); 
      opacity: 0.8;
    }
  }

  @keyframes float-slow {
    0%, 100% { 
      transform: translateY(0px) rotate(0deg); 
    }
    50% { 
      transform: translateY(-20px) rotate(5deg); 
    }
  }

  @keyframes fade-in-scale {
    0% {
      opacity: 0;
      transform: scale(0.9);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes pulse-glow {
    0%, 100% { 
      opacity: 1; 
      filter: brightness(1);
    }
    50% { 
      opacity: 0.8; 
      filter: brightness(1.3);
    }
  }

  @keyframes gradient-shift {
    0% { 
      background-position: 0% 50%; 
    }
    50% { 
      background-position: 100% 50%; 
    }
    100% { 
      background-position: 0% 50%; 
    }
  }

  @keyframes scroll-down {
    0% {
      transform: translateY(0);
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      transform: translateY(12px);
      opacity: 0;
    }
  }

  @keyframes bounce-slow {
    0%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    50% {
      transform: translateX(-50%) translateY(-10px);
    }
  }

  .animate-blob {
    animation: blob 9s infinite;
  }

  .animate-float {
    animation: float 8s ease-in-out infinite;
  }

  .animate-float-slow {
    animation: float-slow 10s ease-in-out infinite;
  }

  .animate-fade-in-scale {
    animation: fade-in-scale 0.6s ease-out;
  }

  .animate-pulse-glow {
    animation: pulse-glow 2s ease-in-out infinite;
  }

  .animate-gradient-shift {
    animation: gradient-shift 6s ease infinite;
  }

  .animate-scroll-down {
    animation: scroll-down 2s ease-in-out infinite;
  }

  .animate-bounce-slow {
    animation: bounce-slow 3s ease-in-out infinite;
  }

  .animation-delay-1000 {
    animation-delay: 1s;
  }

  .animation-delay-2000 {
    animation-delay: 2s;
  }

  .animation-delay-3000 {
    animation-delay: 3s;
  }

  .animation-delay-4000 {
    animation-delay: 4s;
  }

  .animation-delay-5000 {
    animation-delay: 5s;
  }

  .animation-delay-6000 {
    animation-delay: 6s;
  }

  .bg-gradient-radial {
    background: radial-gradient(circle at center, var(--tw-gradient-stops));
  }

  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`}</style>

      <main id="main-content" className="scroll-mt-24 container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 lg:py-12 sm:scroll-mt-28">

            {/* Blog Grid */}
            <section className="mb-12">
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-gray-100 uppercase tracking-wide mb-8 lg:mb-10 text-center">
                LATEST BLOGS
              </h2>
              {filtered.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No blogs found matching your search.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 lg:gap-6">
                  {filtered.slice((currentPage - 1) * postsPerPage, currentPage * postsPerPage).map((post) => (
                    <article
                      key={post.id}
                      className="post_item group flex h-full flex-col overflow-hidden rounded-xl border border-gray-100 bg-white dark:bg-slate-900 shadow-md ring-1 ring-black/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-blue-500/10"
                    >
                      {/* Post Featured Image */}
                      <div className="post_featured shrink-0">
                        <div className="post_thumb aspect-[16/10] overflow-hidden sm:aspect-[5/3] lg:aspect-[16/10]">
                          <Link to={`/blog/${post.id}`} className="block h-full">
                            <img
                              key={post.featuredImage}
                              src={
                                post.featuredImage ||
                                "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=480&h=300&fit=crop"
                              }
                              alt={post.title}
                              className="h-full w-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-105"
                              loading="lazy"
                              decoding="async"
                            />
                          </Link>
                        </div>
                      </div>

                      {/* Post Content */}
                      <div className="post_content flex flex-1 flex-col p-3 sm:p-4">
                        <div className="post_info mb-2">
                          <span className="post_info_item post_info_posted flex items-center gap-1.5 text-xs text-gray-500 sm:text-sm">
                            <span className="text-xs" aria-hidden>
                              📅
                            </span>
                            <Link
                              to={`/blog/${post.id}`}
                              className="post_info_date font-medium hover:text-blue-600 transition-colors"
                            >
                              {new Date(post.date).toLocaleDateString()}
                            </Link>
                          </span>
                        </div>

                        <h3 className="post_title mb-2">
                          <Link
                            to={`/blog/${post.id}`}
                            className="line-clamp-2 text-[15px] font-semibold leading-snug text-gray-900 dark:text-gray-100 transition-colors group-hover:text-blue-700 sm:text-base"
                          >
                            {post.title}
                          </Link>
                        </h3>

                        <div className="post_descr mt-auto flex flex-1 flex-col pt-1">
                          <p className="mb-3 line-clamp-3 flex-1 text-xs leading-relaxed text-gray-600 dark:text-gray-400 sm:text-sm">
                            {post.excerpt}
                          </p>
                          <Link
                            to={`/blog/${post.id}`}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:from-blue-700 hover:to-blue-800 hover:shadow-md sm:w-auto sm:self-start sm:px-4"
                          >
                            Read More
                            <span className="text-xs" aria-hidden>
                              →
                            </span>
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>


            <nav aria-label="Pagination" className="flex items-center justify-center gap-3 mb-16">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-full shadow-md transition transform duration-150 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                  currentPage === 1
                    ? "bg-blue-100 text-blue-300 cursor-not-allowed opacity-80"
                    : "bg-white dark:bg-slate-900 text-blue-700 hover:bg-blue-50 hover:shadow-lg hover:scale-105"
                }`}
              >
                Previous
              </button>
              <div className="flex items-center gap-2">
                {pageNumbers.map((item, idx) =>
                  typeof item === "number" ? (
                    <button
                      key={`pg-${item}-${idx}`}
                      onClick={() => handlePageChange(item)}
                      aria-current={currentPage === item ? "page" : undefined}
                      className={`px-3 py-2 rounded-full shadow-md font-semibold transition transform duration-150 focus:outline-none focus:ring-2 ${
                        currentPage === item
                          ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg scale-105"
                          : "bg-white dark:bg-slate-900 text-blue-700 hover:bg-blue-50 hover:shadow-lg hover:scale-105"
                      }`}
                    >
                      {item}
                    </button>
                  ) : (
                    <span
                      key={`sep-${idx}`}
                      className="px-3 py-2 text-slate-400 select-none"
                      aria-hidden="true"
                    >
                      {item}
                    </span>
                  )
                )}
              </div>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-full shadow-md transition transform duration-150 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                  currentPage === totalPages
                    ? "bg-blue-100 text-blue-300 cursor-not-allowed opacity-80"
                    : "bg-white dark:bg-slate-900 text-blue-700 hover:bg-blue-50 hover:shadow-lg hover:scale-105"
                }`}
              >
                Next
              </button>
            </nav>
      </main>









      <Footer />


    </div>
  );
}
